import pageOverlay from "../../utils/pageOverlay";

/**
 * @file
 * PDP detail section module.
 * Manages behavior for PDP (Product Detail Section), including:
 * - section tab switching on desktop
 * - opening/closing mobile drawer on mobile
 *
 * @author Glory Wiguno <glory@relab.com.au>
 */

// ---- Constants ----
const mobileLayoutBreakpoint = 768;
const detailNavSel = "js-details-tab-switcher";
const bp = window.matchMedia("(min-width:" + mobileLayoutBreakpoint + "px)");
const drawerActiveClass = "pdp-details__block-wrapper--drawer-active";
const productRangeId = "details-product-range";
const defaultNavTabId = "details-specification";

// ---- Variables ----
let detailRoot;
let detailNavs;
let currentActiveTab;
let currentActiveTabNav;
let defaultNavTab;
let mobileDrawer;
let isMobileDrawerOpen;
let titleContainer;
let mobileHeader;
let tabWrapper;

const init = function () {
  detailRoot = document.getElementById("pdp-details");

  if (detailRoot === undefined) {
    return;
  }

  // sets and search for the element that are relevant for the component
  mobileDrawer = document.getElementById("mobile-drawer");
  titleContainer = document.getElementById("mobile-header-title");
  tabWrapper = document.getElementById("pdp-tab-wrapper");
  mobileHeader = detailRoot.querySelector(".pdp-details__mobile-header");
  currentActiveTab = detailRoot.querySelector(".pdp-details-block--active");
  currentActiveTabNav = detailRoot.querySelector(
    ".pdp-details__nav-item--active"
  );
  isMobileDrawerOpen = false;

  initDetailNavs();
  initCloseDrawerBtns();

  // adds listener to matchmedia listener to close the mobile drawer when
  // so that it would not expand by default on mobile
  bp.addListener(closeMobileDrawer);

  window.addEventListener("resize", function () {
    if (isMobile() && isMobileDrawerOpen) {
      adjustMobileDrawerHeight();
    }
  });

  window.addEventListener("pageshow", function () {
    const userType = document
      .querySelector("#hwu-rebate-calculator")
      .getAttribute("data-user-type");
    const isNz = window.location.host.indexOf(".co.nz") > -1;
    if (userType === "MAX_USER" && !isNz) {
      document.querySelector("#hwu-rebate-calculator").style.display = "block";
    }
  });
};

/**
 * @name initDetailNavs
 * @function
 * init tab swithcer navigation for detail section
 *
 */
const initDetailNavs = function () {
  detailNavs = detailRoot.querySelectorAll("." + detailNavSel);

  detailNavs.forEach((nav) => (nav.onclick = handleDetailNavClick));

  if (!isMobile()) {
    setFirstTabAsActive();
  }
};

const setFirstTabAsActive = function () {
  if (detailNavs && detailNavs.length >= 1) {
    const firstNav = detailNavs[0];
    const firstNavTabId = firstNav.getAttribute("data-tab-id");

    if (firstNavTabId !== "details-product-range") {
      switchTabNav(firstNav);
      switchTab(firstNavTabId);

      showProductDetails();
    }
  }
};

/**
 * @name handleDetailNavClick
 * @function
 * click handler for detail nav cta.
 * tab switcher nav have two required attributes:
 * - [data-tab-id]: corresponding id for linked tab element
 * - [data-tab-name]: corresponding tab name for linked element
 *
 * @param {Event} e - event that is passed into the function
 */
const handleDetailNavClick = function (e) {
  const self = e.currentTarget;

  switchTabNav(self);
  const switchTo = self.getAttribute("data-tab-id");

  switchTab(switchTo);
};

/**
 * @name switchTabNav
 * @function
 * handle tab nav switching operations
 *
 * @param {el} el - tab nav element
 */
const switchTabNav = function (el) {
  const activeClass = "pdp-details__nav-item" + "--active";

  if (currentActiveTabNav) {
    currentActiveTabNav.classList.remove(activeClass);
  }

  currentActiveTabNav = el;
  currentActiveTabNav.classList.add(activeClass);
};

/**
 * @name switchTab
 * @function
 * handle tab section switching for pdp details section.
 * In mobile layout, switchTab will also open mobile drawer.
 *
 * @param {String} tabId - the tab id for the tab that would change to active state
 */
const switchTab = function (tabId) {
  if (tabWrapper === undefined) {
    // console.warn("Tab wrapper element does not exist.");
    return;
  }

  const activeClass = "pdp-details-block" + "--active";

  // in the case of product range section is called when it is in desktop layout
  if (tabId === productRangeId && !isMobile()) {
    return;
  }

  if (currentActiveTab) {
    currentActiveTab.classList.remove(activeClass);
  }

  const newTab = document.getElementById(tabId);
  if (newTab === undefined) {
    // console.warn("Tab with id '" + tabId + "' does not exist.");
    return;
  }

  currentActiveTab = newTab;

  if (currentActiveTab) {
    currentActiveTab.classList.add(activeClass);

    const newTitle = currentActiveTab.getAttribute("data-tab-name");
    changeTitle(newTitle);
  }

  // do operation to open mobile drawer only if it is under a certain viewport
  // width
  if (isMobile()) {
    openMobileDrawer();
  }
};

const isMobile = function () {
  return window.innerWidth < mobileLayoutBreakpoint;
};

/**
 * @name initCloseDrawerBtns
 * @function
 * initialise buttons for closing mobile drawer
 */
const initCloseDrawerBtns = function () {
  const closeBtns = detailRoot.querySelector(".js-pdp-mobile-drawer-close");

  closeBtns.onclick = closeMobileDrawer;
};

/**
 * @name openMobileDrawer
 * @function
 * handle the operation for opening mobile drawer
 *
 */
const openMobileDrawer = function () {
  if (mobileDrawer === undefined) {
    // console.warn("PDP Mobile Drawer does not exist.");
    return;
  }

  showProductDetails();

  if (!isMobileDrawerOpen) {
    mobileDrawer.classList.add(drawerActiveClass);

    isMobileDrawerOpen = true;
    adjustMobileDrawerHeight();
    if (isMobile()) {
      pageOverlay.open();
    }
  }
};

/**
 * @name closeMobileDrawer
 * @function
 * handle the operation for closing mobile drawer
 *
 * @param {Event} e - event object that gets passed into the function
 */
const closeMobileDrawer = function (e) {
  if (mobileDrawer === undefined) {
    // console.warn("PDP Mobile Drawer does not exist.");
    return;
  }

  // checks if last active tab on mobile is product range block.
  // if it is product range block, then change to default block.
  // Since product range block will be persistent on desktop
  // this happens when this function is called on resize by matchMedia
  if (e !== undefined && e.type !== "click") {
    if (currentActiveTab === document.getElementById("details-product-range")) {
      defaultNavTab =
        defaultNavTab ||
        document.querySelector(`[data-tab-id="${defaultNavTabId}"]`);

      if (defaultNavTab) {
        switchTab(defaultNavTabId);
        switchTabNav(defaultNavTab);
      }
    }
  }
  hideMobileDrawerIfOpen();
};

const hideMobileDrawerIfOpen = function () {
  if (isMobileDrawerOpen) {
    mobileDrawer.classList.remove(drawerActiveClass);

    isMobileDrawerOpen = false;
    tabWrapper.removeAttribute("style");
    pageOverlay.close();
  }
};

/**
 * @name changeTitle
 * @function
 * changes the title for section heading/mobile header title
 *
 * @param {String} text - new text content for title container
 */
const changeTitle = function (text) {
  if (titleContainer === undefined) {
    // console.warn("Title container element does not exist.");
    return;
  }

  mobileHeader.classList.remove("hidden");

  titleContainer.textContent = text;
};

const showProductDetails = function () {
  mobileDrawer.classList.remove("hidden");
};

/**
 * @name adjustMobileDrawerHeight
 * @function
 * adjust mobile drawer inner container height on mobile to fill the rest
 * of the screen based on mobile header height;
 *
 */
const adjustMobileDrawerHeight = function () {
  const viewportHeight = window.innerHeight;
  const mobileHeaderHeight = mobileHeader.clientHeight;

  const blockHeight = viewportHeight - mobileHeaderHeight;

  tabWrapper.setAttribute("style", "height: " + blockHeight + "px;");
};

export default { init, switchTab, showProductDetails };
